import { RadioCardItem, RadioCardLabel, RadioCardRoot } from '@/components/ui/radio-card';
import type { InvitableProgramResponse } from '@/types/affiliate-programs';
import { Badge } from '@chakra-ui/react';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'iconoir-react';

/**
 * Controlled program radio list: the parent owns the selection (`selected`) and
 * receives every pick through `setSelectedProgram` — no mirrored local state.
 */
const ProgramSelection = ({
  programs,
  selected,
  setSelectedProgram,
}: {
  programs: InvitableProgramResponse[];
  selected: Partial<InvitableProgramResponse>;
  setSelectedProgram: (program: Partial<InvitableProgramResponse>) => void;
}) => {
  const { t } = useTranslation();

  // Pre-select the program when it is the only invitable one, so a single-choice
  // step arms the primary action without an extra click. No-op when there are
  // zero or several selectable programs, or once the brand has already picked.
  const selectedId = selected.id;
  useEffect(() => {
    if (selectedId) return;
    const invitable = programs.filter((program) => program.canInvite);
    if (invitable.length === 1) {
      setSelectedProgram(invitable[0]);
    }
  }, [programs, selectedId, setSelectedProgram]);

  return (
    <RadioCardRoot
      value={selectedId ?? ''}
      onValueChange={(details) => {
        const selectedProgramId = details.value;
        if (selectedProgramId) {
          const selectedProgram = programs.find((program) => program.id === selectedProgramId);
          if (selectedProgram) {
            setSelectedProgram(selectedProgram);
          }
        }
      }}
      maxW="full"
      spaceY={1}
    >
      <RadioCardLabel fontWeight="normal">
        {t('invitations.modal.programSelection.label')}
      </RadioCardLabel>
      {programs.map((program) => {
        const isDisabled = !program.canInvite;
        const statusLabel = program.canInvite
          ? t('invitations.modal.programSelection.status.available')
          : t('invitations.modal.programSelection.status.invited');
        const statusColor = program.canInvite ? 'green' : 'gray';

        return (
          <RadioCardItem
            key={program.id}
            value={program.id}
            label={program.name}
            indicatorPlacement="start"
            rounded="lg"
            colorPalette="primary"
            disabled={isDisabled}
            _disabled={{
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
            rightElement={
              <Badge
                colorPalette={statusColor}
                variant={program.canInvite ? 'subtle' : 'solid'}
                size="sm"
              >
                {!program.canInvite && <Check width={16} height={16} />}
                {statusLabel}
              </Badge>
            }
          />
        );
      })}
    </RadioCardRoot>
  );
};

export default ProgramSelection;
